/* ==========================================================================
   AutocompleteRxjs > Client > Suggestions
   ========================================================================== */

/* Modules */
const { decode } = require('he');
const { html } = require('common-tags');

const { toNode } = require('@bluekiri/alexandria/helpers/domHelpers');
const { randomId } = require('@bluekiri/alexandria/helpers/dataHelpers');
const ViewHelpers = require('@bluekiri/alexandria/core/ViewHelpers');

const { fromEvent } = require('rxjs');
const { map, tap, filter } = require('rxjs/operators');

const { actions, types } = require('../store/actions');

const TooltipTemplate = require('../../../templates/tooltipPanel');
const TooltipClient = require('../../../../tooltip/client/js');

const { initKeyboard, destroyKeyboard } = require('./keyboard');

const { collectionByGroup } = require('../helpers/data');

const { ICONS } = require('../../../constants');
const configuration = require('../config');

const {
    hooks: { suggestionsList: hookSuggestionsList }
} = configuration();

/* Render suggestion */
const renderSuggestion = (data, context) => {
    /* Data */
    const { text, index, query } = data;

    /* View helpers */
    const { markdown } = ViewHelpers(context);

    /* Regex to look for query, remove invalid chars parenthesis and forward slash */
    const regex = new RegExp(query.replace(/[\\{()}]/g, ''), 'gi');

    const suggestionText = markdown(text.replace(regex, str => `**${str}**`));

    return html(`
        <p class='${configuration().css.suggestion}' data-autocomplete-suggestion=${index}>
            ${suggestionText}
        </p>
    `);
};

/* Render group */
const renderGroup = (data, literals, context) => {
    /* Data */
    const { group = {}, query = '' } = data;

    /* Suggestions */
    const { key = '', suggestions = [] } = group;

    /* View helpers */
    const { icon } = ViewHelpers(context);

    return html(`
        <p class='${configuration().css.header}'>
            ${icon(ICONS[key])}
            <span>${literals[key] || ''}</span>
        </p>
        ${html(suggestions.map(item => renderSuggestion({ ...item, query }, context)))}
    `);
};

/* Render groups */
const renderGroups = (data, literals, context) => {
    const { suggestions = [], query = '' } = data;

    /* Exit when no suggestions */
    if (suggestions.length < 1) return '';

    return html(collectionByGroup(suggestions).map(group => renderGroup({ group, query }, literals, context)));
};

/* Render suggestions */
const renderSuggestions = (data, literals, context) =>
    html(`
        <div class='autocomplete-rxjs__list' ${hookSuggestionsList}>
            ${renderGroups(data, literals, context)}
        </div>
    `);

/* Create */
function createSuggestions(instance) {
    /* Data */
    const { config = {}, store = {} } = instance;
    const { context = {}, literals = {} } = config;
    const { suggestions = [], query } = store.select('autocomplete');

    /* Exit when no suggestions */
    if (suggestions.length < 1) return false;

    /* -Template */
    return renderSuggestions({ suggestions, query }, literals, context);
}

function createPanel(instance, content) {
    const { config = {}, store = {} } = instance;
    const { context = {}, placement } = config;

    /* Data */
    const { tooltipType: type } = store.select('autocomplete');

    /* Prepare tooltip data */
    const dataTooltip = {
        placement,
        type,
        content: html(content)
    };

    return toNode(TooltipTemplate(dataTooltip, context))[0];
}

function initSuggestions(instance) {
    const panel = instance;

    const { store = {} } = instance;
    const { input } = instance.hooks;

    /* Data */
    const { tooltipType } = store.select('autocomplete');

    /* Init tooltip */
    panel.tooltip = TooltipClient(`[data-component="Tooltip"][data-type="${tooltipType}"]`, {
        trigger: input
    });

    if (!panel.tooltip) return;

    /* Update hooks */
    panel.hooks = {
        ...instance.hooks,
        list: panel.tooltip.element.querySelector('[data-list]')
    };

    /* Extend actions */
    panel.actions = {
        ...instance.actions,
        changeValue: data => instance.store.dispatch(actions.changeValue(data))
    };

    /* Extend Reactions */
    panel.reactions = {
        ...instance.reactions,
        changeValue: instance.store.subscribe((state, action) => {
            if (action.type !== types.CHANGE_VALUE) return;

            /* Dispatch close panel suggestions */
            panel.actions.closeSuggestions();
            /* Sync input value */
            panel.actions.syncValue();
        })
    };

    /* Update subscriptions */
    panel.subscriptions = {
        ...instance.subscriptions,
        clicks: fromEvent(instance.hooks.list, 'click')
            .pipe(
                tap(event => event.stopPropagation()),
                map(event => event.target.closest('[data-autocomplete-suggestion]')),
                filter(element => !!element),
                map(element => +element.dataset.autocompleteSuggestion),
                map(index => instance.store.select('autocomplete').suggestions[index])
            )
            .subscribe(value => {
                /* Dispatch */
                instance.actions.changeValue(value);
            })
    };

    /* Keyboard */
    initKeyboard(instance);
}

/* Add */
function addSuggestions(instance) {
    const panel = instance;

    const { config = {}, hooks = {} } = instance;
    const { appendToParent } = config;

    const { input } = hooks;

    /* Create Suggestions */
    const suggestionsTemplate = createSuggestions(instance);

    /* no suggestions -> close suggestions */
    if (!suggestionsTemplate) {
        /* Close suggestion and sync input value */
        panel.actions.closeSuggestions();
        panel.actions.syncValue();
        return;
    }

    /* Add tooltip with suggestions to DOM */
    if (appendToParent) input.parentNode.appendChild(createPanel(instance, suggestionsTemplate));
    else document.body.appendChild(createPanel(instance, suggestionsTemplate));

    initSuggestions(instance);
}

/* Update suggestions */
function updateSuggestions(instance) {
    const panel = instance;

    /* Data */
    const { suggestions, query } = instance.store.select('autocomplete');
    const { literals, context } = instance.config;

    /* -Template */
    const template = renderGroups({ suggestions, query }, literals, context);

    panel.hooks.list.innerHTML = template;
}

function destroyTooltip(instance) {
    const panel = instance;

    if (!instance.tooltip) return;

    /* Destroy component */
    instance.tooltip.actions.destroy();

    /* Remove references */
    delete panel.tooltip;
}

/* Destroy suggestions */
function destroySuggestions(instance) {
    const panel = instance;
    if (!instance.tooltip) return;

    /* Destroy */
    destroyTooltip(instance);

    /* Actions */
    delete panel.actions.changeValue;

    /* Reactions */
    instance.reactions.changeValue.unsubscribe();
    delete panel.reactions.changeValue;

    /* Subscriptions */
    instance.subscriptions.clicks.unsubscribe();
    delete panel.subscriptions.clicks;

    /* Keyboard */
    destroyKeyboard(instance);
}

function startSuggestions(instance) {
    const panel = instance;
    const { hooks = {}, config = {} } = panel;
    const { hasPlaceHolder } = config;
    const { input: inputEl } = hooks;

    /* Extends actions */
    panel.actions = {
        ...panel.actions,
        openSuggestions: () => {
            const tooltipType = randomId(randomId(configuration().type.tooltip));

            return instance.store.dispatch(actions.openSuggestions(tooltipType));
        },
        closeSuggestions: () => instance.store.dispatch(actions.closeSuggestions()),
        setSuggestions: data => {
            /**
             * Decode the special characters from the API
             */
            const suggestions = data.map(item => ({ ...item, text: decode(item.text) }));

            return instance.store.dispatch(actions.setSuggestions(suggestions));
        }
    };

    /* Extends reactions */
    panel.reactions = {
        ...panel.reactions,
        clear: instance.store.subscribe((state, action) => {
            if (action.type !== types.CLEAR) return;

            /* Data */
            const { error, active, isLoading } = panel.store.select('autocomplete');

            /* Toggle input label position */
            if (inputEl.value === '') {
                if (!hasPlaceHolder && active) panel.actions.toggleActive();
                if (error && !isLoading) panel.actions.resetStatus();
            }
        }),
        closeSuggestions: instance.store.subscribe((state, action) => {
            if (action.type !== types.CLOSE_SUGGESTIONS) return;

            /* Destroy suggestions */
            destroySuggestions(panel);
        }),
        openSuggestions: instance.store.subscribe((state, action) => {
            if (action.type !== types.OPEN_SUGGESTIONS) return;

            /* Add panel suggestions */
            addSuggestions(instance);
        }),
        setSuggestions: instance.store.subscribe((state, action) => {
            if (action.type !== types.SET_SUGGESTIONS) return;

            /* Data */
            const { openSuggestions, suggestions } = instance.store.select('autocomplete');

            if (!suggestions.length) return;

            if (!openSuggestions) {
                instance.actions.openSuggestions();
                return;
            }

            /* Update UI suggestions */
            updateSuggestions(instance);
        })
    };
}
/* Exports */
module.exports = {
    updateSuggestions,
    addSuggestions,
    destroySuggestions,
    startSuggestions
};
